import {
  Component,
  inject,
  signal,
} from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ApiService } from '../../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Socket } from 'ngx-socket-io';
import {
  TAGInvHistoryEvent,
  TAGInvHistoryEventType,
  TAGInvItemInstance,
  TAGInvItemType,
  TAGInvTransaction,
} from '../../../../models/taginventory';
import { MessageService } from 'primeng/api';
import { DatePipe } from '@angular/common';
import { MatIcon } from '../../../components/mat-icon/mat-icon';
import { TAGUser } from '../../../../models/taguser';
import { dialogConfig } from '../../../dialogs';
import {
  TransactionEditDialog,
  TransactionEditDialogData,
} from '../transaction-edit-dialog/transactionEditDialog';
import { TimelineModule } from 'primeng/timeline';
import { EmptyStateComponent } from '../../../components/empty-state/empty-state.component';

export interface ItemHistoryDialogData {
  item: TAGInvItemType;
  instanceid: string;
  socket: Socket;
}

@Component({
  selector: 'app-internal-item-history-dialog',
  imports: [
    SkeletonModule,
    DialogModule,
    ButtonModule,
    TranslatePipe,
    MatIcon,
    DatePipe,
    TimelineModule,
    EmptyStateComponent
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './itemHistoryDialog.html',
  styleUrl: './itemHistoryDialog.scss',
})
export class ItemHistoryDialog {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  public api = inject(ApiService);
  //public printer = inject(PrinterService);

  item = signal<TAGInvItemType>(null);
  instance = signal<TAGInvItemInstance>(null);
  transactions = signal<TAGInvTransaction[]>([]);
  loadedTransactions = signal<Record<number, boolean>>({});
  users = signal<TAGUser[]>([]);
  loadedUsers = signal<Record<number, boolean>>({});

  constructor(
    public dialogRef: DynamicDialogRef<ItemHistoryDialog>,
    public config: DynamicDialogConfig<ItemHistoryDialogData>,
  ) {
    config.header = this.translate.translate('internal.inventory.itemHistory.title')() + config.data.item.name;

    this.item.set(config.data.item);
    this.instance.set(
      this.item().instances.find((instance) => instance.itemid === config.data.instanceid),
    );
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      if (this.instance().history) {
        this.instance.update((i) => {
          let newI = new TAGInvItemInstance(i.serialize());
          newI.history.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          return newI;
        });

        for (let historyEvent of this.instance().history) {
          if (historyEvent.transactionid) {
            this.api
              .getInvTransaction(historyEvent.transactionid)
              .then((result) => {
                this.transactions.update((t) => [...t, result]);
                this.loadedTransactions.update((lt) => {
                  let newLT = Object.assign({}, lt);
                  newLT[this.instance().history.indexOf(historyEvent)] = true;
                  return newLT;
                });
              })
              .catch((reason) => {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translate.translate('errors.couldNotLoadInventoryTransactions')(),
                  detail: reason.message,
                });
              });
          }

          if (historyEvent.userid) {
            this.api
              .getUser(historyEvent.userid)
              .then((result) => {
                this.users.update((u) => [...u, result]);
                this.loadedUsers.update((lu) => {
                  let newLU = Object.assign({}, lu);
                  newLU[this.instance().history.indexOf(historyEvent)] = true;
                  return newLU;
                });
              })
              .catch((reason) => {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translate.translate('errors.couldNotLoadUsers')(),
                  detail: reason.message,
                });
              });
          }
        }
      }
    });
  }

  transactionById(id: string): TAGInvTransaction {
    return this.transactions().find((transaction) => transaction._id === id);
  }

  userById(id: string): TAGUser {
    return this.users().find((user) => user._id === id);
  }

  eventIcon(eventtype: TAGInvHistoryEventType): string {
    switch (eventtype) {
      case TAGInvHistoryEventType.CREATE:
        return 'add';
      case TAGInvHistoryEventType.UPDATE:
        return 'edit';
      case TAGInvHistoryEventType.DELETE:
        return 'delete';
      case TAGInvHistoryEventType.CHECKOUT:
        return 'logout';
      case TAGInvHistoryEventType.CHECKIN:
        return 'login';
      default:
        return 'help';
    }
  }

  eventMessage(event: TAGInvHistoryEvent): string {
    if (event.message) return event.message;

    switch (event.eventtype) {
      case TAGInvHistoryEventType.CREATE:
        return 'internal.inventory.itemHistory.create';
      case TAGInvHistoryEventType.UPDATE:
        return 'internal.inventory.itemHistory.update';
      case TAGInvHistoryEventType.DELETE:
        return 'internal.inventory.itemHistory.delete';
      case TAGInvHistoryEventType.CHECKOUT:
        return 'internal.inventory.itemHistory.checkout';
      case TAGInvHistoryEventType.CHECKIN:
        return 'internal.inventory.itemHistory.checkin';
      default:
        return 'No message';
    }
  }

  showTransaction(transactionid: string) {
    const transactionEditRef = this.dialogService.open(
      TransactionEditDialog,
      dialogConfig<TransactionEditDialogData>({
        closable: true,
        data: {
          transactionId: transactionid,
          socket: this.config.data.socket,
        },
      }),
    );

    this.dialogRef.close();
  }
}
